import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Minus, Plus, Pencil, Trash2, Leaf, Check, Play, Pause, Repeat, Activity } from "lucide-react";

const NEXT = { inhale: "hold_in", hold_in: "exhale", exhale: "hold_out", hold_out: "inhale" };

function fmtDur(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? `${m}m${s > 0 ? ` ${s}s` : ""}` : `${s}s`;
}

export default function BreathworkTab() {
  const t = useT();
  const [list, setList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [inhale, setInhale] = useState(4);
  const [holdIn, setHoldIn] = useState(4);
  const [exhale, setExhale] = useState(6);
  const [holdOut, setHoldOut] = useState(0);
  const [mode, setMode] = useState("reps");
  const [reps, setReps] = useState(8);
  const [durationS, setDurationS] = useState(180);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/breathwork-techniques"); setList(Array.isArray(data) ? data : []); }
    catch { setList([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setEditingId(null); setName(""); setInhale(4); setHoldIn(4); setExhale(6); setHoldOut(0); setMode("reps"); setReps(8); setDurationS(180); setNotes(""); };

  const startEdit = (r) => {
    setEditingId(r.id); setName(r.name); setInhale(r.inhale_s); setHoldIn(r.hold_after_inhale_s); setExhale(r.exhale_s); setHoldOut(r.hold_after_exhale_s);
    if (r.duration_s) { setMode("duration"); setDurationS(r.duration_s); setReps(8); } else { setMode("reps"); setReps(r.repetitions || 8); setDurationS(180); }
    setNotes(r.notes || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const oneCycle = inhale + holdIn + exhale + holdOut;
  const estimated = useMemo(() => (mode === "reps" ? oneCycle * reps : durationS), [mode, oneCycle, reps, durationS]);
  const canSave = name.trim().length > 0 && inhale >= 1 && exhale >= 1 && (mode === "reps" ? reps >= 1 : durationS >= 15);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const body = { name: name.trim(), inhale_s: inhale, hold_after_inhale_s: holdIn, exhale_s: exhale, hold_after_exhale_s: holdOut, notes: notes.trim() };
    if (mode === "reps") { body.repetitions = reps; body.duration_s = null; } else { body.duration_s = durationS; body.repetitions = null; }
    try {
      if (editingId) await api.patch(`/coach/breathwork-techniques/${editingId}`, body);
      else await api.post("/coach/breathwork-techniques", body);
      toast.success(t("bw.saved")); reset(); await load();
    } catch (err) { toast.error(apiError(err, t("bw.save_failed"))); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    if (!window.confirm(t("bw.delete_confirm", { name: r.name }))) return;
    try { await api.delete(`/coach/breathwork-techniques/${r.id}`); if (editingId === r.id) reset(); toast.success(t("bw.deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="cm-breathwork-tab">
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="bw-editor-card">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{editingId ? t("bw.edit_existing") : t("bw.new")}</p>

        <label className="mb-1.5 block text-sm font-medium text-foreground">{t("bw.name")}</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t("bw.name_ph")} data-testid="bw-name" />

        <label className="mb-1.5 mt-4 block text-sm font-medium text-foreground">{t("bw.rhythm")}</label>
        <div className="grid grid-cols-4 gap-2">
          <Stepper label={t("bw.inhale")} value={inhale} onChange={setInhale} min={1} max={60} testID="bw-inhale" />
          <Stepper label={t("bw.hold_in")} value={holdIn} onChange={setHoldIn} min={0} max={60} testID="bw-hold-in" />
          <Stepper label={t("bw.exhale")} value={exhale} onChange={setExhale} min={1} max={60} testID="bw-exhale" />
          <Stepper label={t("bw.hold_out")} value={holdOut} onChange={setHoldOut} min={0} max={60} testID="bw-hold-out" />
        </div>

        <BreathPreview inhale={inhale} holdIn={holdIn} exhale={exhale} holdOut={holdOut} oneCycle={oneCycle} t={t} />

        <label className="mb-1.5 mt-4 block text-sm font-medium text-foreground">{t("bw.length")}</label>
        <div className="flex gap-1 rounded-full bg-secondary p-1">
          <button onClick={() => setMode("reps")} data-testid="bw-mode-reps" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium ${mode === "reps" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Repeat className="h-4 w-4" /> {t("bw.by_reps")}</button>
          <button onClick={() => setMode("duration")} data-testid="bw-mode-duration" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium ${mode === "duration" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Activity className="h-4 w-4" /> {t("bw.by_duration")}</button>
        </div>
        <div className="mt-3 max-w-[220px]">
          {mode === "reps"
            ? <Stepper label={t("bw.reps")} value={reps} onChange={setReps} min={1} max={200} testID="bw-reps" wide />
            : <Stepper label={t("bw.duration_s")} value={durationS} onChange={setDurationS} min={15} max={3600} step={15} testID="bw-duration" wide />}
        </div>
        <p className="mt-2 text-xs italic text-muted-foreground">{t("bw.estimated")}: {fmtDur(estimated)}</p>

        <label className="mb-1.5 mt-4 block text-sm font-medium text-foreground">{t("bw.notes")}</label>
        <Textarea className="min-h-[60px]" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} placeholder={t("bw.notes_ph")} data-testid="bw-notes" />

        <div className="mt-4 flex justify-end gap-2">
          {editingId && <Button variant="outline" className="rounded-full" onClick={reset} data-testid="bw-cancel">{t("common.cancel")}</Button>}
          <Button className="gap-1.5 rounded-full" onClick={save} disabled={!canSave || saving} data-testid="bw-save"><Check className="h-4 w-4" /> {saving ? t("common.saving") : editingId ? t("common.save") : t("bw.save_new")}</Button>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("bw.saved_list")} {list && list.length > 0 ? `(${list.length})` : ""}</p>
        {list === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : list.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Leaf className="h-6 w-6" /></div>
            <p className="mt-3 text-sm text-muted-foreground">{t("bw.empty")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((r) => (
              <div key={r.id} data-testid={`bw-item-${r.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Leaf className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{r.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary"><Activity className="h-2.5 w-2.5" /> {r.cycle}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary"><Repeat className="h-2.5 w-2.5" /> {r.repetitions ? `${r.repetitions}×` : fmtDur(r.duration_s || 0)}</span>
                  </div>
                  {r.notes && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.notes}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => startEdit(r)} data-testid={`bw-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" onClick={() => del(r)} data-testid={`bw-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ label, value, onChange, min, max, step = 1, testID, wide }) {
  return (
    <div className={wide ? "w-full" : ""}>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(Math.max(min, value - step))} disabled={value <= min} data-testid={testID ? `${testID}-dec` : undefined} className="flex h-9 w-8 items-center justify-center rounded-lg border border-border bg-background disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
        <div className="flex h-9 flex-1 items-center justify-center rounded-lg border border-border bg-background font-display text-sm font-semibold text-foreground" data-testid={testID ? `${testID}-value` : undefined}>{value}</div>
        <button onClick={() => onChange(Math.min(max, value + step))} disabled={value >= max} data-testid={testID ? `${testID}-inc` : undefined} className="flex h-9 w-8 items-center justify-center rounded-lg border border-border bg-background disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

function BreathPreview({ inhale, holdIn, exhale, holdOut, oneCycle, t }) {
  const [running, setRunning] = useState(true);
  const [phase, setPhase] = useState("inhale");
  const [left, setLeft] = useState(inhale);
  const [scale, setScale] = useState(0.6);
  const [dur, setDur] = useState(inhale);
  const timer = useRef(null);
  const countdown = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (countdown.current) clearInterval(countdown.current);
    if (!running) { setScale(0.6); return; }
    let cancelled = false;
    const step = (p) => {
      if (cancelled) return;
      const d = p === "inhale" ? inhale : p === "hold_in" ? holdIn : p === "exhale" ? exhale : holdOut;
      const target = p === "inhale" || p === "hold_in" ? 1 : 0.6;
      setPhase(p); setDur(Math.max(d, 0.3)); setScale(target); setLeft(Math.max(d, 0));
      if (countdown.current) clearInterval(countdown.current);
      let l = d;
      countdown.current = setInterval(() => { l -= 1; setLeft(l < 0 ? 0 : l); }, 1000);
      timer.current = setTimeout(() => step(NEXT[p]), d === 0 ? 50 : d * 1000);
    };
    step("inhale");
    return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); if (countdown.current) clearInterval(countdown.current); };
  }, [inhale, holdIn, exhale, holdOut, running]);

  const phaseLabel = phase === "inhale" ? t("bw.inhale") : phase === "hold_in" ? t("bw.hold_in") : phase === "exhale" ? t("bw.exhale") : t("bw.hold_out");

  return (
    <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="bw-preview">
      <div className="relative flex h-40 w-40 items-center justify-center">
        <div className="absolute h-40 w-40 rounded-full border border-dashed border-primary/40" />
        <div className="flex h-32 w-32 flex-col items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg" style={{ transform: `scale(${scale})`, transition: `transform ${dur}s ease-in-out` }}>
          <span className="text-xs font-semibold uppercase tracking-wider">{phaseLabel}</span>
          <span className="font-display text-xl font-semibold">{left > 0 ? `${left}s` : "·"}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground"><Activity className="h-3 w-3 text-primary" /> {inhale}-{holdIn}-{exhale}-{holdOut}</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground">{oneCycle}s / {t("bw.cycle")}</span>
        <button onClick={() => setRunning((r) => !r)} data-testid="bw-preview-toggle" className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/50 bg-primary/10 text-primary">{running ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}</button>
      </div>
    </div>
  );
}
